// Pure decision logic extracted from the two video players, ahead of the v1
// consolidation (docs/features/v1/video-player.md). These functions contain NO
// DOM, no YouTube IFrame calls, and no Stimulus — only the maths and branching
// that decide *what* the player should do. They are unit-tested in
// spec/javascript/player_logic.test.js so the consolidation can be proven
// behaviour-preserving without driving the real (cross-origin) YouTube iframe.
//
// Each function cites the exact source it replicates so the extraction can be
// reviewed for fidelity:
//   C = app/javascript/controllers/course_lesson_controller.js
//   W = app/javascript/controllers/video_controller.js
//
// During Phase 1 the consolidated controller should IMPORT these functions
// rather than re-deriving the rules inline — that is what makes this suite a
// guard rather than a parallel reimplementation.

// --- Segment model -------------------------------------------------------

// C:233-234 — isSegmentMode()
export function isSegmentMode(segmentMode, endTime) {
  return Boolean(segmentMode) && endTime > 0
}

// C:137-145 — start position when (re)loading the video. Returns the seconds to
// pass as `startSeconds`, or `undefined` when no explicit start is set (player
// starts at 0). Resume wins only when it falls *inside* the segment window;
// otherwise we fall back to the segment start (full-video: segStart 0 ⇒ undefined).
export function resumeStartSeconds(lastPosition, startTime, endTime) {
  const resumePos = lastPosition
  const segStart = startTime || 0
  const segEnd = endTime
  if (resumePos > segStart && (segEnd === 0 || resumePos < segEnd)) {
    return resumePos
  }
  if (segStart > 0) {
    return segStart
  }
  return undefined
}

// --- Boundary enforcement ------------------------------------------------

// C:113-120 — seek-while-paused via native controls. Returns the corrected time
// to seek to, or null when no correction is needed. No-op outside segment mode.
export function seekWhilePausedTarget(time, startTime, endTime, segment) {
  if (!segment) return null
  if (time < startTime) return startTime
  if (time > endTime) return endTime
  return null
}

// C:200-210 — the 100ms boundary poll during playback. Models the two
// independent ifs in source; for valid segments (startTime < endTime) only one
// fires. 'seek' ⇒ snap back to start; 'end' ⇒ stop + pause + interstitial.
export function playbackBoundaryAction(current, startTime, endTime, segment) {
  if (!segment) return { type: 'none' }
  if (current < startTime) return { type: 'seek', target: startTime }
  if (current >= endTime) return { type: 'end', target: endTime }
  return { type: 'none' }
}

// --- Skip & frame-step ---------------------------------------------------

// C:518-519 — ±10s forward, clamped to segment end in segment mode.
export function skipForwardTarget(time, endTime, segment) {
  const target = time + 10
  return segment ? Math.min(target, endTime) : target
}

// C:532-533 — ±10s backward, clamped to segment start (or 0 in full mode).
export function skipBackwardTarget(time, startTime, segment) {
  const target = time - 10
  return segment ? Math.max(target, startTime) : Math.max(0, target)
}

// C:32 — one frame at ~30fps.
export const FRAME_STEP = 0.033

// C:591-593 — one frame forward while paused, clamped to segment end.
export function stepForwardTarget(time, startTime, endTime, segment, frameStep = FRAME_STEP) {
  const target = time + frameStep
  return segment ? Math.min(target, endTime) : target
}

// C:605-607 — one frame backward while paused, clamped to segment start (or 0).
export function stepBackwardTarget(time, startTime, endTime, segment, frameStep = FRAME_STEP) {
  const target = time - frameStep
  return segment ? Math.max(target, startTime) : Math.max(0, target)
}

// --- Speed ---------------------------------------------------------------

// C:462 — auto-mute decision: slow speeds mute, 1x+ unmute.
// DECISION (Phase 3, 2026-06-19): KEEP auto-mute below 1x (slowed audio is
// distorted). The earlier "likely remove" note in video-player.md is resolved
// in favour of keeping it.
export function speedShouldMute(speed) {
  return speed < 1.0
}

// --- Scrubber maths ------------------------------------------------------

// C:265 — current playback time → 0..1 fill fraction.
export function scrubberPosition(currentTime, duration) {
  return duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0
}

// C:342 — pointer x within the scrubber rect → 0..1 fraction.
export function positionFromClientX(clientX, rectLeft, rectWidth) {
  return Math.max(0, Math.min(1, (clientX - rectLeft) / rectWidth))
}

// C:347-349 — scrubber fraction → seek target seconds, clamped to segment.
export function seekPositionToTime(position, duration, startTime, endTime, segment) {
  let targetTime = position * duration
  if (segment) {
    targetTime = Math.max(startTime, Math.min(endTime, targetTime))
  }
  return targetTime
}

// C:271, 374, 378 — fraction (0..1) → CSS width/left string.
export function pctString(fraction) {
  return (fraction * 100).toFixed(2) + '%'
}

// C:364-380 — disabled pre/post scrubber zones. Both hidden outside segment
// mode or with no known duration.
export function disabledZones(startTime, endTime, duration, segment) {
  if (!segment || duration <= 0) {
    return { preHidden: true, postHidden: true, preWidth: null, postWidth: null }
  }
  const startFraction = startTime / duration
  const endFraction = endTime / duration
  return {
    preWidth: pctString(startFraction),
    preHidden: startFraction <= 0,
    postWidth: pctString(1 - endFraction),
    postHidden: endFraction >= 1,
  }
}

// C:383-393 — seconds → M:SS, or H:MM:SS past an hour.
export function formatTime(seconds) {
  if (!seconds || seconds <= 0) return '0:00'
  const totalSeconds = Math.floor(seconds)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${m}:${String(s).padStart(2, '0')}`
}

// --- Watch-page analytics (video_controller.js) --------------------------

// W:106 — round a timestamp to a tenth of a second.
export function toTenth(time) {
  return parseFloat(time.toFixed(1))
}

// W:34-38 — on PLAY, snap the recorded start back to the last stop point when
// they are within 0.5s, so a resume doesn't open a spurious analytics gap.
export function resolvePlayStartTime(time, lastStopTime) {
  return Math.abs(lastStopTime - time) < 0.5 ? lastStopTime : time
}

// --- Resume vs. start (Netflix model, Phase 3) ---------------------------
// video-player.md "Resume vs. play-from-start". The saved position is the
// student's LAST position (per-(student, video), VideoResumePosition). These
// pin the decision so both contexts behave identically and the rules are tested.

export const RESUME_STALE_MS = 90 * 24 * 60 * 60 * 1000 // ~3 months
export const RESUME_NEAR_END_SECONDS = 15               // within this of the end ⇒ outro, don't offer
export const RESUME_MIN_PROGRESS_SECONDS = 5            // less than this in ⇒ not worth a prompt

// Decide whether to OFFER resume, and which choice is the DEFAULT (primary).
// Returns { offer, default: 'resume'|'start', resumeSeconds, startSeconds, stale }.
//   - offer: a meaningful saved position exists, inside the playable window, and
//     not near the end (outro). Outside the segment window ⇒ no offer (respect
//     the segment, as the course player does today), start at the segment start.
//   - default: 'start' when the position is stale (> ~3 months) — but resume is
//     still offered (default ≠ hide); otherwise 'resume'.
//   - startSeconds: where "Start from the beginning" seeks — the segment start in
//     segment mode, else 0.
export function resumeDecision({
  savedPosition,
  savedWatchedAtMs = null,
  nowMs,
  startTime = 0,
  endTime = 0,
  segment = false,
  duration = 0,
  staleMs = RESUME_STALE_MS,
  nearEndSeconds = RESUME_NEAR_END_SECONDS,
  minProgressSeconds = RESUME_MIN_PROGRESS_SECONDS
} = {}) {
  const segStart = startTime || 0
  const startSeconds = segStart
  const stale = savedWatchedAtMs != null && nowMs != null &&
    (nowMs - savedWatchedAtMs) > staleMs

  const noOffer = { offer: false, default: 'start', resumeSeconds: startSeconds, startSeconds, stale }

  if (savedPosition == null || !(savedPosition > 0)) return noOffer

  // Effective end of the playable window: segment end in segment mode, else the
  // video duration (Infinity when unknown — near-end check can't fire).
  const effectiveEnd = (segment && endTime > 0) ? endTime : (duration > 0 ? duration : Infinity)

  const meaningful = (savedPosition - segStart) >= minProgressSeconds
  const insideWindow = savedPosition > segStart && savedPosition < effectiveEnd
  const nearEnd = savedPosition >= (effectiveEnd - nearEndSeconds)

  if (!meaningful || !insideWindow || nearEnd) return noOffer

  return {
    offer: true,
    default: stale ? 'start' : 'resume',
    resumeSeconds: savedPosition,
    startSeconds,
    stale
  }
}
